import React, { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { DocumentTitle } from '@wcj/tools-react-components';
import { pageTitle } from './config/siteSeo';
import Layout from './app/Layout';
import { NoMatch } from './app/NoMatch';
import { Loading } from './app/Loading';

interface LoadableProps {
  title?: string;
}

const Loadable =
  (Component: React.LazyExoticComponent<() => JSX.Element>) =>
  ({ title, ...props }: LoadableProps) => {
    const fullTitle = title ? pageTitle(title) : undefined;
    return (
      <DocumentTitle title={fullTitle}>
        <Suspense fallback={<Loading />}>
          <Component />
        </Suspense>
      </DocumentTitle>
    );
  };

// @ts-ignore
const HomePage = Loadable(lazy(() => import('./app/Home')));
// @ts-ignore
const UrlEncode = Loadable(lazy(() => import('@wcj/tools-react-url-encode')));
const TextCase = Loadable(lazy(() => import('@wcj/tools-react-text-case')));
const JSONFormat = Loadable(lazy(() => import('@wcj/tools-react-json-format')));
const ImageToBase64 = Loadable(lazy(() => import('@wcj/tools-react-image-to-base64')));

const Hash = Loadable(lazy(() => import('@wcj/tools-react-hash')));
const GeneratePassword = Loadable(lazy(() => import('@wcj/tools-react-generate-password')));
const HtmlToMarkdown = Loadable(lazy(() => import('@wcj/tools-react-html-to-markdown')));
const MarkdownToHtml = Loadable(lazy(() => import('@wcj/tools-react-markdown-to-html')));
const HTMLEscape = Loadable(lazy(() => import('@wcj/tools-react-html-escape')));
const Base64Encoder = Loadable(lazy(() => import('@wcj/tools-react-base64-encoder')));
const QRCodeGenerator = Loadable(lazy(() => import('@wcj/tools-react-qrcode-generator')));
const ChinesePinyinConversion = Loadable(lazy(() => import('@wcj/tools-react-chinese-pinyin-conversion')));
const CodeToImage = Loadable(lazy(() => import('@wcj/tools-react-code-to-image')));
// @ts-ignore
const GenerateGithubBadges = Loadable(lazy(() => import('@wcj/tools-react-generate-github-badges')));
// @ts-ignore
const TomlToJSON = Loadable(lazy(() => import('@wcj/tools-react-toml-to-json')));
// @ts-ignore
const CSSEasingAnimation = Loadable(lazy(() => import('@wcj/tools-react-css-easing-animation')));
// @ts-ignore
const SimpleMarkdownEditor = Loadable(lazy(() => import('@wcj/tools-react-simple-markdown-editor')));
// @ts-ignore
const JWTDecoder = Loadable(lazy(() => import('@wcj/tools-react-jwt-decoder')));
// @ts-ignore
const JSON2Yaml = Loadable(lazy(() => import('@wcj/tools-react-json-yaml')));
// @ts-ignore
const JSON2XML = Loadable(lazy(() => import('@wcj/tools-react-json-xml')));
const PDFToImg = Loadable(lazy(() => import('@wcj/tools-react-pdf-to-img')));
const ExifViewer = Loadable(lazy(() => import('@wcj/tools-react-exif-viewer')));
const JSON2INI = Loadable(lazy(() => import('@wcj/tools-react-json-ini')));
const JSBeautifier = Loadable(lazy(() => import('@wcj/tools-react-js-beautifier')));
const CSSFormatter = Loadable(lazy(() => import('@wcj/tools-react-css-formatter')));
const HTMLFormatter = Loadable(lazy(() => import('@wcj/tools-react-html-formatter')));
const SVGOptimizer = Loadable(lazy(() => import('@wcj/tools-react-svg-optimizer')));
const AmountInRMB = Loadable(lazy(() => import('@wcj/tools-react-amount-in-rmb')));
const SimplifiedTraditionalChinese = Loadable(lazy(() => import('@wcj/tools-react-simplified-traditional-chinese')));
const StandardChineseCharactersRMB = Loadable(lazy(() => import('@wcj/tools-react-standard-chinese-characters')));
const JSObfuscator = Loadable(lazy(() => import('@wcj/tools-react-js-obfuscator')));

export let routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'home',
        element: <HomePage />,
      },
      {
        path: 'url-encode',
        element: <UrlEncode title="URL Encoder/Decoder" />,
      },
      {
        path: 'json-format',
        element: <JSONFormat title="JSON Format" />,
      },
      {
        path: 'js-beautifier',
        element: <JSBeautifier title="JS Beautifier/Uglify" />,
      },
      {
        path: 'js-obfuscator',
        element: <JSObfuscator title="JS Obfuscator" />,
      },
      {
        path: 'text-case',
        element: <TextCase title="Text Case" />,
      },
      {
        path: 'image-to-base64',
        element: <ImageToBase64 title="Image To Base64" />,
      },
      {
        path: 'code-to-image',
        element: <CodeToImage title="Code To Image" />,
      },
      {
        path: 'hash',
        element: <Hash title="Hash" />,
      },
      {
        path: 'generate-password',
        element: <GeneratePassword title="Generate Password" />,
      },
      {
        path: 'html-formatter',
        element: <HTMLFormatter title="HTML Formatter/Minify" />,
      },
      {
        path: 'html-escape',
        element: <HTMLEscape title="HTML Escape/Unescape" />,
      },
      {
        path: 'html-to-markdown',
        element: <HtmlToMarkdown title="HTML To Markdown" />,
      },
      {
        path: 'markdown-to-html',
        element: <MarkdownToHtml title="Markdown To HTML" />,
      },
      {
        path: 'base64-encoder',
        element: <Base64Encoder title="Base64 Encoder/Decoder Tool" />,
      },
      {
        path: 'qrcode-generator',
        element: <QRCodeGenerator title="QRCode Generator Tool" />,
      },
      {
        path: 'chinese-pinyin-conversion',
        element: <ChinesePinyinConversion title="Chinese Pinyin Conversion Tool" />,
      },
      {
        path: 'generate-github-badges',
        element: <GenerateGithubBadges title="Generate Github Badges Tool" />,
      },
      {
        path: 'toml-to-json',
        element: <TomlToJSON title="Toml To JSON Tool" />,
      },
      {
        path: 'css-easing-animation',
        element: <CSSEasingAnimation title="CSS Easing Animation Tool" />,
      },
      {
        path: 'css-formatter',
        element: <CSSFormatter title="CSS Formatter/Minify Tool" />,
      },
      {
        path: 'simple-markdown-editor',
        element: <SimpleMarkdownEditor title="Simple Markdown Editor Tool" />,
      },
      {
        path: 'jwt-decoder',
        element: <JWTDecoder title="JWT Decoder Tool" />,
      },
      {
        path: 'json-yaml',
        element: <JSON2Yaml title="JSON/YAML Converter Tool" />,
      },
      {
        path: 'json-xml',
        element: <JSON2XML title="JSON/XML Converter Tool" />,
      },
      {
        path: 'json-ini',
        element: <JSON2INI title="JSON/ini Converter Tool" />,
      },
      {
        path: 'pdf-to-img',
        element: <PDFToImg title="PDF To Image Tool" />,
      },
      {
        path: 'exif-viewer',
        element: <ExifViewer title="Exif Viewer Tool" />,
      },
      {
        path: 'svg-optimizer',
        element: <SVGOptimizer title="SVG Optimizer Tool" />,
      },
      {
        path: 'amount-in-rmb',
        element: <AmountInRMB title="Amount In RMB Tool" />,
      },
      {
        path: 'standard-chinese-characters',
        element: <StandardChineseCharactersRMB title="Table of General Standard Chinese Characters" />,
      },
      {
        path: 'simplified-traditional-chinese',
        element: <SimplifiedTraditionalChinese title="Simplified Traditional Chinese Converter" />,
      },
      { path: '*', element: <NoMatch /> },
    ],
  },
  { path: '*', element: <NoMatch /> },
];
